import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, getDocs, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useCompany } from '../../context/CompanyContext';
import { useAuth } from '../../context/AuthContext';
import ConversationItem from './ConversationItem';
import { Loader2, Inbox as InboxIcon, Plus, Search, X, CheckCircle2 } from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

const FILTERS = {
  ALL:      'all',
  UNREAD:   'unread',
  MINE:     'mine',
  RESOLVED: 'resolved',
};

// Number of conversations to load per page. Increasing this on "Load More"
// re-subscribes with a larger limit — old sub unsubscribes automatically.
const PAGE_SIZE = 30;

/** Strip everything except digits for a loose phone-number match. */
const digitsOnly = (s) => (s || '').replace(/\D/g, '');

/** Return true if the conversation matches the search query. */
const matchesSearch = (c, q) => {
  if (!q) return true;
  const lq = q.trim().toLowerCase();

  // Name match
  if ((c.displayName || c.clientName || '').toLowerCase().includes(lq)) return true;

  // Phone match — normalized (strip non-digits) so +1(555)123-4567 matches "5551234567"
  const rawPhone = c.clientPhone || '';
  if (rawPhone.toLowerCase().includes(lq)) return true;
  const dq = digitsOnly(lq);
  if (dq.length >= 4 && digitsOnly(rawPhone).includes(dq)) return true;

  // Last-message content
  if ((c.lastMessage || '').toLowerCase().includes(lq)) return true;

  return false;
};

const ConversationList = ({ selectedConversation, onSelectConversation, onStartNewConversation }) => {
  const { companyId } = useCompany();
  const { user } = useAuth();
  const currentUserId = user?.uid || null;

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(FILTERS.ALL);
  const [search, setSearch] = useState('');
  const [mentionedById, setMentionedById] = useState({});
  const isFirstLoadRef = useRef(true);

  // Full conversation list fetched once when search is active and hasMore is true.
  const [allConversations, setAllConversations] = useState(null);
  const [fetchingAll, setFetchingAll]           = useState(false);
  const allFetchedForRef = useRef('');

  // Resolved tab — separate one-time fetch (no real-time needed for archive browsing)
  const [resolvedConvs,    setResolvedConvs]    = useState([]);
  const [resolvedLoading,  setResolvedLoading]  = useState(false);

  const fetchAllConversations = useCallback(async () => {
    if (!companyId || !db || fetchingAll) return;
    setFetchingAll(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'companies', companyId, 'conversations'),
          orderBy('lastMessageTime', 'desc')
        )
      );
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllConversations(all);
      allFetchedForRef.current = companyId;
    } catch (e) {
      console.error('[Inbox] Failed to fetch full conversation list for search', e);
      // Fall back silently — search will use paginated set
    } finally {
      setFetchingAll(false);
    }
  }, [companyId, fetchingAll]);

  // When the user starts typing and the loaded set is incomplete, fetch all once.
  useEffect(() => {
    if (
      search.trim() &&
      hasMore &&
      allConversations === null &&
      !fetchingAll &&
      allFetchedForRef.current !== companyId
    ) {
      fetchAllConversations();
    }
    // Clear the full-list cache when the company changes
    if (allFetchedForRef.current && allFetchedForRef.current !== companyId) {
      setAllConversations(null);
      allFetchedForRef.current = '';
    }
  }, [search, hasMore, allConversations, fetchingAll, companyId, fetchAllConversations]);

  useEffect(() => {
    if (!db || !companyId) {
      // Not ready yet; AppReadyGate will prevent this provider from mounting.
      return;
    }

    // Full-page spinner only on the very first mount — not on Load More expansions
    if (isFirstLoadRef.current) setLoading(true);

    const conversationsRef = collection(db, 'companies', companyId, 'conversations');
    const q = query(
      conversationsRef,
      orderBy('lastMessageTime', 'desc'),
      limit(displayLimit)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const conversationsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setConversations(conversationsList);
        // If the server returned exactly displayLimit docs, more likely exist server-side
        setHasMore(snapshot.docs.length === displayLimit);
        isFirstLoadRef.current = false;
        setLoading(false);
        setLoadingMore(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching conversations:', err);
        setError('Failed to load conversations');
        setLoading(false);
        setLoadingMore(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, displayLimit]);

  // Expand the subscription limit without resetting the selected conversation or
  // causing a scroll jump — new items append below the current list in sort order.
  const handleLoadMore = () => {
    setLoadingMore(true);
    setDisplayLimit((prev) => prev + PAGE_SIZE);
  };

  // Compute which conversations mention the current user by scanning messages subcollections.
  useEffect(() => {
    const loadMentions = async () => {
      if (!companyId || !db || !currentUserId || conversations.length === 0) {
        setMentionedById({});
        return;
      }
      try {
        const result = {};
        await Promise.all(
          conversations.map(async (conv) => {
            try {
              const messagesRef = collection(
                db,
                'companies',
                companyId,
                'conversations',
                conv.id,
                'messages'
              );
              const q = query(
                messagesRef,
                where('mentions', 'array-contains', currentUserId)
              );
              const snap = await getDocs(q);
              if (!snap.empty) {
                result[conv.id] = true;
              }
            } catch (e) {
              // Best-effort only; do not block inbox if mention lookup fails.
              console.error('Failed to compute mentions for conversation', conv.id, e);
            }
          })
        );
        setMentionedById(result);
      } catch (e) {
        console.error('Failed to compute mentions map', e);
      }
    };

    loadMentions();
  }, [companyId, currentUserId, conversations]);

  // ── Resolved tab subscription ─────────────────────────────────────────────
  // Fires whenever the user switches to the Resolved tab.
  // Uses a real-time subscription; falls back to a one-time getDocs if the
  // composite index isn't deployed yet.
  useEffect(() => {
    if (filter !== FILTERS.RESOLVED || !db || !companyId) return;

    setResolvedLoading(true);
    const convRef = collection(db, 'companies', companyId, 'conversations');

    // Try real-time subscription with orderBy (needs composite index)
    let unsub;
    try {
      const q = query(
        convRef,
        where('status', '==', 'resolved'),
        orderBy('lastMessageTime', 'desc'),
        limit(50)
      );
      unsub = onSnapshot(
        q,
        (snap) => {
          setResolvedConvs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setResolvedLoading(false);
        },
        () => {
          // Composite index missing — fall back to getDocs without orderBy
          getDocs(query(convRef, where('status', '==', 'resolved')))
            .then((snap) => {
              const docs = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => {
                  const ta = a.lastMessageTime?.toDate?.()?.getTime?.() || 0;
                  const tb = b.lastMessageTime?.toDate?.()?.getTime?.() || 0;
                  return tb - ta;
                });
              setResolvedConvs(docs);
              setResolvedLoading(false);
            })
            .catch(() => setResolvedLoading(false));
        }
      );
    } catch {
      unsub = () => {};
    }
    return () => unsub?.();
  }, [filter, companyId]);

  const processedConversations = useMemo(() => {
    // When search is active and we have the full list, search over all conversations.
    // Otherwise fall back to the paginated set.
    const source =
      search.trim() && allConversations !== null
        ? allConversations
        : conversations;

    let items = source.map((c) => {
      const unread = (c.unreadCount || 0) > 0;
      const lastMessageInbound = c.lastMessageFrom === 'client';
      const assignedToMe = currentUserId && c.assignedToUserId === currentUserId;
      const mentioned = !!mentionedById[c.id];
      const lastMessageTime = c.lastMessageTime?.toDate
        ? c.lastMessageTime.toDate()
        : c.lastMessageTime
        ? new Date(c.lastMessageTime)
        : null;

      return {
        ...c,
        _signals: {
          unread,
          lastMessageInbound,
          assignedToMe,
          mentioned,
          lastMessageTime,
        },
      };
    });

    // Exclude resolved conversations from the open list.
    // (The Resolved tab has its own separate subscription.)
    items = items.filter((c) => (c.status || 'open') !== 'resolved');

    // Active-tab filters (All / Unread / Mine)
    items = items.filter((c) => {
      const s = c._signals;
      switch (filter) {
        case FILTERS.UNREAD:  return s.unread;
        case FILTERS.MINE:    return s.assignedToMe;
        case FILTERS.ALL:
        default:              return true;
      }
    });

    // Search — uses shared matchesSearch helper with phone normalization
    if (search.trim()) {
      items = items.filter((c) => matchesSearch(c, search));
    }

    // Sorting priority:
    // 1) Mentioned
    // 2) Unread inbound (unread && lastMessageInbound)
    // 3) Recently active (lastMessageTime desc)
    // 4) Assigned to me
    items.sort((a, b) => {
      const sa = a._signals;
      const sb = b._signals;

      if (sa.mentioned !== sb.mentioned) {
        return sa.mentioned ? -1 : 1;
      }

      const aUnreadInbound = sa.unread && sa.lastMessageInbound;
      const bUnreadInbound = sb.unread && sb.lastMessageInbound;
      if (aUnreadInbound !== bUnreadInbound) {
        return aUnreadInbound ? -1 : 1;
      }

      const ta = sa.lastMessageTime ? sa.lastMessageTime.getTime() : 0;
      const tb = sb.lastMessageTime ? sb.lastMessageTime.getTime() : 0;
      if (tb !== ta) {
        return tb - ta;
      }

      if (sa.assignedToMe !== sb.assignedToMe) {
        return sa.assignedToMe ? -1 : 1;
      }

      return 0;
    });

    return items;
  }, [conversations, allConversations, mentionedById, filter, search, currentUserId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const totalCount  = conversations.length;
  const unreadCount = conversations.filter(c => (c.unreadCount || 0) > 0).length;

  const handleReopen = async (convId) => {
    if (!companyId) return;
    try {
      await updateDoc(
        doc(db, 'companies', companyId, 'conversations', convId),
        { status: 'open', reopenedAt: serverTimestamp() }
      );
      // Remove from local resolved list immediately (subscription will catch up)
      setResolvedConvs((prev) => prev.filter((c) => c.id !== convId));
    } catch (err) {
      console.error('[ConversationList] Failed to reopen conversation', err);
    }
  };

  const TABS = [
    { key: FILTERS.ALL,      label: 'Open',     badge: null },
    { key: FILTERS.UNREAD,   label: 'Unread',   badge: unreadCount > 0 ? unreadCount : null },
    { key: FILTERS.MINE,     label: 'Mine',     badge: null },
    { key: FILTERS.RESOLVED, label: 'Resolved', badge: null },
  ];

  return (
    <div className="h-full flex flex-col overflow-x-hidden">
      {/* ── Top bar: title + new button ────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-base font-semibold text-gray-900 truncate min-w-0">Conversations</h2>
          {onStartNewConversation && (
            <button
              type="button"
              onClick={onStartNewConversation}
              data-testid="new-conversation-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#0f4c81] text-white hover:bg-[#0f4c81]/90 transition-colors flex-shrink-0 whitespace-nowrap"
            >
              <Plus className="h-3.5 w-3.5" />
              New
            </button>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, or message…"
            data-testid="inbox-search-input"
            className="w-full pl-8 pr-8 py-1.5 border border-gray-200 rounded-lg text-xs
              focus:outline-none focus:ring-1 focus:ring-[#0f4c81] bg-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              data-testid="inbox-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {/* Search status row */}
        {search.trim() && (
          <div className="flex items-center justify-between mb-2 text-[11px] text-gray-400">
            <span data-testid="inbox-search-count">
              {fetchingAll
                ? 'Searching all conversations…'
                : `${processedConversations.length} result${processedConversations.length !== 1 ? 's' : ''}${
                    allConversations !== null
                      ? ` across all ${allConversations.length}`
                      : ` in ${conversations.length} loaded`
                  }`}
            </span>
          </div>
        )}
        <div
          className="flex items-center gap-2 lg:gap-1 overflow-x-auto flex-nowrap max-w-full -mx-1 px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="inbox-filter-tabs"
        >
          {TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              data-testid={`filter-tab-${tab.key}`}
              className={`
                flex-shrink-0 flex items-center gap-1.5 px-3.5 lg:px-3 py-1.5 lg:py-1
                rounded-full text-xs font-semibold lg:font-medium transition-colors
                ${filter === tab.key
                  ? 'bg-[#0f4c81] text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800 active:bg-gray-200'
                }
              `}
            >
              {tab.label}
              {tab.badge != null && (
                <span className={`
                  inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold leading-none
                  ${filter === tab.key ? 'bg-white/25 text-white' : 'bg-blue-500 text-white'}
                `}>
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── List ─────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {/* ── Resolved tab ─────────────────────────────────────────────────── */}
        {filter === FILTERS.RESOLVED ? (
          resolvedLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
            </div>
          ) : resolvedConvs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-sm gap-2">
              <CheckCircle2 className="w-6 h-6 opacity-40" />
              <span>No resolved conversations</span>
            </div>
          ) : (
            resolvedConvs.map((conv) => (
              <div key={conv.id} className="relative group">
                <ConversationItem
                  conversation={conv}
                  selected={selectedConversation?.id === conv.id}
                  onSelect={onSelectConversation}
                />
                {/* Re-open button — appears on hover */}
                <button
                  type="button"
                  data-testid={`reopen-btn-${conv.id}`}
                  onClick={(e) => { e.stopPropagation(); handleReopen(conv.id); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2
                    opacity-0 group-hover:opacity-100 transition-opacity
                    text-xs bg-white border border-gray-200 rounded-md px-2 py-1
                    text-gray-600 hover:bg-gray-50 hover:text-gray-900 shadow-sm font-medium"
                >
                  Re-open
                </button>
              </div>
            ))
          )
        ) : (
          /* ── Open / Unread / Mine tabs ─────────────────────────────────── */
          processedConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-sm gap-2">
              <InboxIcon className="w-6 h-6 opacity-40" />
              <span>
                {search.trim()
                  ? 'No matches found'
                  : filter === FILTERS.UNREAD
                  ? 'No unread conversations'
                  : filter === FILTERS.MINE
                  ? 'None assigned to you'
                  : 'No open conversations'}
              </span>
            </div>
          ) : (
            <>
              {processedConversations.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  selected={selectedConversation?.id === conversation.id}
                  onSelect={onSelectConversation}
                />
              ))}

              {hasMore && !search.trim() && (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  data-testid="load-more-conversations-btn"
                  className="w-full py-3 text-xs text-[#0f4c81] hover:bg-gray-50 flex items-center justify-center gap-2 border-t border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading…</>
                  ) : (
                    'Load older conversations'
                  )}
                </button>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

export default ConversationList;
